/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useRef, useMemo } from 'preact/hooks';
import { memo } from 'preact/compat';
import { Keys, containsKey, isSameKey } from '../utils/UNSAFE_keys';
import { classNames } from '../utils/UNSAFE_classNames';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import {
  CurrentItemVariant,
  ListItemRendererContext,
  Metadata,
  SelectionDetail
} from '../UNSAFE_Collection';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { useId } from '../hooks/UNSAFE_useId';
import { Roles, ItemPadding, Props as ListProps } from '../UNSAFE_ListView';
import { getItemContext } from '../utils/PRIVATE_collectionUtils';
import { isMultipleSelection } from './ListUtils';
import { getListViewRoles } from './ListRoles';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { listItemStyles, listItemMultiVariantStyles, paddingStyles } from './themes/ListStyles.css';

/**
 * Props for the ListViewItem Component
 */
export type Props<K, D> = {
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  context?: ListItemRendererContext<K, D>;
  itemKey: K;
  itemData: D;
  itemMetadata: Metadata<K>;
  itemIndex: number;
  itemDepth?: number;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  isActive: boolean;
  isGridlineVisible: boolean;
  selectedKeys: Keys<K>;
  handleSelectionChange: (detail: SelectionDetail<K>) => void;
  isTopGridlineVisible?: boolean;
  isTabbable?: boolean;
  role: Roles;
  selectionMode: ListProps<K, D>['selectionMode'];
  suggestion?: 'end' | true;
  currentItemVariant?: CurrentItemVariant;
  padding?: 'enabled' | 'disabled' | ItemPadding;
};

/**
 * The internal component used to render a single item in ListView.
 */
export function ListViewItem<K extends string | number, D>({
  children,
  context,
  itemKey,
  itemData,
  itemMetadata,
  itemIndex,
  itemDepth,
  isFocused,
  isFocusRingVisible,
  isActive,
  isGridlineVisible,
  selectedKeys,
  handleSelectionChange,
  isTopGridlineVisible = false,
  isTabbable = false,
  role,
  selectionMode,
  suggestion,
  currentItemVariant,
  padding
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const roles = useMemo(() => getListViewRoles(role), [role]);
  const isSelected = containsKey(selectedKeys, itemKey);

  // some roles come with related aria attributes which need to be set
  const cellRoleAttributes =
    roles.cell === 'option'
      ? {
          role: roles.cell,
          'aria-posinset': itemIndex + 1,
          'aria-setsize': -1
        }
      : {
          role: roles.cell
        };

  // don't bubble Arrow Up and Down events to parent when in isTabbable mode
  // this bubbling causes the outer CollectionFocus ring to change index while in the isTabbable mode.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (isTabbable && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.stopPropagation();
    }
  };

  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle({ disabledKeys: ['enter'] });

  const variantClasses = listItemMultiVariantStyles({
    selectable: isSelectable(selectionMode, isSelected) ? 'isSelectable' : 'notSelectable',
    selected: isSelected ? 'isSelected' : 'notSelected',
    selectedSingle:
      isSelected && selectionMode === 'single' ? 'isSelectedSingle' : 'notSelectedSingle',
    needsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
    pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
    active: applyActiveStyle || isActive ? 'isActive' : 'notActive',
    focusHighlight:
      currentItemVariant === 'highlight' && isFocused ? 'isFocusHighlight' : 'notFocusHighlight',
    focusRingVisible:
      isFocusRingVisible && isFocused ? 'isFocusRingVisible' : 'notFocusRingVisible',
    gridlineTop: isTopGridlineVisible ? 'visible' : 'hidden',
    gridlineBottom:
      isGridlineVisible ||
      (suggestion === 'end' && !isGridlineVisible && (!isSelected || selectionMode !== 'single'))
        ? 'visible'
        : 'hidden'
  });
  const paddingClasses = [];
  if (padding) {
    if (padding === 'enabled') {
      paddingClasses.push(paddingStyles.allPadding);
    } else if (padding !== 'disabled') {
      if (padding.top === 'enabled') {
        paddingClasses.push(paddingStyles.topItemPadding);
      }
      if (padding.bottom === 'enabled') {
        paddingClasses.push(paddingStyles.bottomItemPadding);
      }
      if (padding.start === 'enabled') {
        paddingClasses.push(paddingStyles.startItemPadding);
      }
      if (padding.end === 'enabled') {
        paddingClasses.push(paddingStyles.endItemPadding);
      }
    }
  }

  const classes = classNames([variantClasses, ...paddingClasses]);
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const { class: cls, ...cellStyles } = styleInterpolations({
    flex: '1 1 auto',
    alignSelf: 'center'
  });
  const cellClasses = classNames([
    cls,
    isMultipleSelection(selectionMode) && listItemStyles.checkboxContainer
  ]);

  const rendererContext = context
    ? context
    : getItemContext<K, D>(
        { data: { data: itemData, metadata: itemMetadata }, index: itemIndex },
        selectedKeys,
        isMultipleSelection(selectionMode),
        handleSelectionChange
      );

  // todo: use translated text for selector
  // note cannot put focus on gridcell div since JAWS will not read the aria-rowindex
  // correctly, so unfortunately needed another div
  return (
    <div
      aria-rowindex={role === 'grid' ? itemIndex + 1 : undefined}
      aria-level={itemDepth}
      data-oj-key={itemKey}
      data-oj-list-item=""
      class={classes}
      ref={rootRef}
      role={roles.item}
      {...(suggestion && { 'data-oj-suggestion': true })}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}
      {...interactionProps}>
      <div
        id={useId()}
        style={cellStyles}
        class={cellClasses}
        aria-posinset={role === 'grid' || role === 'treegrid' ? undefined : itemIndex + 1}
        aria-setsize={role === 'grid' || role === 'treegrid' ? undefined : -1}
        onKeyDown={handleKeyDown}
        aria-colindex={role === 'grid' ? 1 : undefined}
        aria-selected={selectionMode !== 'none' ? isSelected : undefined}
        {...cellRoleAttributes}>
        <TabbableModeContext.Provider value={{ isTabbable }}>
          <FocusTrap isDisabled={!isTabbable} restoreFocusRef={false}>
            {children(rendererContext)}
          </FocusTrap>
        </TabbableModeContext.Provider>
      </div>
    </div>
  );
}

/**
 * Helper method to determine whether item is selectable
 * @returns true if item is selectable (show hover effect), false otherwise
 */
const isSelectable = (
  selectionMode: ListProps<string | number, unknown>['selectionMode'],
  isItemSelected: boolean
) => {
  return selectionMode !== 'none' && (isMultipleSelection(selectionMode) || !isItemSelected);
};

export const MemoizeListViewItem = memo(
  ListViewItem,
  (prev: Props<string | number, unknown>, next: Props<string | number, unknown>) => {
    if (prev && next) {
      return (
        prev.children === next.children &&
        prev.itemData === next.itemData &&
        prev.itemKey === next.itemKey &&
        prev.itemMetadata.isLeaf === next.itemMetadata.isLeaf &&
        prev.itemMetadata.parentKey === next.itemMetadata.parentKey &&
        prev.itemMetadata.suggestion === next.itemMetadata.suggestion &&
        prev.itemMetadata.treeDepth === next.itemMetadata.treeDepth &&
        prev.itemIndex === next.itemIndex &&
        prev.isFocused === next.isFocused &&
        prev.isFocusRingVisible === next.isFocusRingVisible &&
        isSameKey(prev.selectedKeys, next.selectedKeys) &&
        prev.isTabbable === next.isTabbable &&
        prev.isActive === next.isActive &&
        prev.selectionMode === next.selectionMode &&
        prev.role === next.role &&
        prev.itemDepth === next.itemDepth &&
        prev.currentItemVariant === next.currentItemVariant &&
        prev.suggestion === next.suggestion &&
        prev.isGridlineVisible === next.isGridlineVisible &&
        prev.isTopGridlineVisible === next.isTopGridlineVisible &&
        prev.padding === next.padding
      );
    }
    return false;
  }
);
